import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { 
  ArrowLeft, ArrowRight, Users, CreditCard, Bell, Wallet, 
  PiggyBank, Plane, Home, Gift, Utensils, CheckCircle,
  Smartphone, QrCode, TrendingUp, Shield, Zap, Heart
} from 'lucide-react';
import { Helmet } from 'react-helmet-async';

const Guide = () => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [activeUseCase, setActiveUseCase] = useState(0);

  const steps = [
    {
      number: "01",
      title: "Create Your Group",
      description: "Start by creating a group for your shared goal. Name it, set your target amount, and choose how often members should contribute.",
      icon: <Users className="w-8 h-8" />,
      details: [
        "Tap 'Create Group' on your dashboard",
        "Give your group a catchy name (e.g., 'Goa Trip 2026')",
        "Set the target amount you want to collect",
        "Choose contribution frequency: Daily, Weekly, or Monthly",
        "Add your UPI ID to receive payments"
      ],
      color: "from-violet-500 to-purple-500"
    },
    {
      number: "02",
      title: "Invite Your Squad",
      description: "Share the invite link with friends and family. They can join instantly with just one tap.",
      icon: <Heart className="w-8 h-8" />,
      details: [
        "Copy your unique group invite link",
        "Share via WhatsApp, Instagram, or any app",
        "Friends click and join in seconds",
        "Track who's joined in real-time",
        "No signup needed for quick joins"
      ],
      color: "from-pink-500 to-rose-500"
    },
    {
      number: "03",
      title: "Contribute via UPI",
      description: "Members contribute their share directly through UPI. Fast, secure, and instant settlement.",
      icon: <QrCode className="w-8 h-8" />,
      details: [
        "Tap 'Contribute' on the group page",
        "Enter your contribution amount",
        "You'll be redirected to your UPI app",
        "Complete payment with your PIN",
        "Contribution is recorded automatically"
      ],
      color: "from-green-500 to-emerald-500"
    },
    {
      number: "04",
      title: "Track & Remind",
      description: "Monitor everyone's contributions and send smart reminders to those who forget.",
      icon: <Bell className="w-8 h-8" />,
      details: [
        "View contribution status of all members",
        "Get automatic reminders before due dates",
        "Admins can send manual reminders",
        "See who's paid and who's pending",
        "Escalating reminders for overdue payments"
      ],
      color: "from-orange-500 to-amber-500"
    },
    {
      number: "05",
      title: "Settle & Celebrate",
      description: "When your goal is reached, settle up and celebrate your achievement together!",
      icon: <TrendingUp className="w-8 h-8" />,
      details: [
        "Track progress towards your goal",
        "View detailed analytics and charts",
        "Export transaction history",
        "Settle any remaining balances",
        "Earn XP and climb the leaderboard!"
      ],
      color: "from-cyan-500 to-blue-500"
    }
  ];

  const useCases = [
    {
      icon: <Home className="w-10 h-10" />,
      title: "Rent & Bills",
      subtitle: "Roommate Expenses",
      description: "Split rent, electricity, WiFi, and maintenance with your flatmates. Set monthly reminders so no one forgets their share.",
      example: "₹25,000 rent split 4 ways = ₹6,250 each",
      tips: ["Set recurring monthly contributions", "Add all utilities in one group", "Use 'Pay for Me' if someone's short"],
      color: "from-blue-500 to-indigo-500",
      gradient: "bg-gradient-to-br from-blue-500/20 to-indigo-500/20"
    },
    {
      icon: <Plane className="w-10 h-10" />,
      title: "Trip Planning",
      subtitle: "Travel with Friends",
      description: "Plan your dream vacation by collecting contributions over time. Track flights, hotels, and activities all in one place.",
      example: "Goa trip: ₹50,000 goal, ₹2,500/week for 20 weeks",
      tips: ["Start collecting 2-3 months early", "Add expenses as you book", "Use chat to discuss plans"],
      color: "from-emerald-500 to-teal-500",
      gradient: "bg-gradient-to-br from-emerald-500/20 to-teal-500/20"
    },
    {
      icon: <Gift className="w-10 h-10" />,
      title: "Group Gifts",
      subtitle: "Birthday & Celebrations",
      description: "Pool money for a special gift without the awkward money collection. Perfect for birthdays, weddings, or farewells.",
      example: "Birthday gift: ₹10,000 from 10 friends = ₹1,000 each",
      tips: ["Keep the group secret from the recipient!", "Set a quick deadline", "Use anonymous contributions"],
      color: "from-pink-500 to-rose-500",
      gradient: "bg-gradient-to-br from-pink-500/20 to-rose-500/20"
    },
    {
      icon: <Utensils className="w-10 h-10" />,
      title: "Food & Dining",
      subtitle: "Office Lunches & Parties",
      description: "Split restaurant bills, office parties, or weekly lunch orders. No more 'I'll pay you later' situations.",
      example: "Team lunch: ₹5,000 split among 8 = ₹625 each",
      tips: ["Create a recurring lunch group", "Add tip in the expense", "Settle weekly for convenience"],
      color: "from-orange-500 to-red-500",
      gradient: "bg-gradient-to-br from-orange-500/20 to-red-500/20"
    },
    {
      icon: <PiggyBank className="w-10 h-10" />,
      title: "Family Savings",
      subtitle: "Shared Goals",
      description: "Save together as a family for big purchases, emergency funds, or festivals. Teach kids financial responsibility.",
      example: "Family vacation fund: ₹1,00,000 over 6 months",
      tips: ["Include all family members", "Celebrate milestones together", "Use gamification for kids"],
      color: "from-violet-500 to-purple-500",
      gradient: "bg-gradient-to-br from-violet-500/20 to-purple-500/20"
    },
    {
      icon: <Wallet className="w-10 h-10" />,
      title: "EMI Splitting",
      subtitle: "Shared Purchases",
      description: "Bought a TV or appliance together? Split the EMI payments fairly across all users over the installment period.",
      example: "55-inch TV EMI: ₹3,000/month split 3 ways",
      tips: ["Set EMI as recurring expense", "Track payment history", "Get reminders before due date"],
      color: "from-cyan-500 to-blue-500",
      gradient: "bg-gradient-to-br from-cyan-500/20 to-blue-500/20"
    }
  ];

  const features = [
    { icon: <Smartphone className="w-6 h-6" />, title: "UPI Payments", desc: "Direct bank transfers" },
    { icon: <Shield className="w-6 h-6" />, title: "Secure", desc: "Bank-grade encryption" },
    { icon: <Zap className="w-6 h-6" />, title: "Instant", desc: "Real-time updates" },
    { icon: <Bell className="w-6 h-6" />, title: "Smart Reminders", desc: "Never miss a payment" }
  ];

  return (
    <div className="min-h-screen bg-[#030712] text-white">
      <Helmet>
        <title>How SharePay Works | Complete Guide to Group Savings India</title>
        <meta name="description" content="Learn how to use SharePay India for splitting bills via UPI, group savings, rent sharing, trip planning. Step-by-step guide for Indians." />
        <link rel="canonical" href="https://sharepay.today/guide" />
        <meta name="robots" content="index, follow" />
      </Helmet>

      {/* Noise Texture Overlay */}
      <div className="fixed inset-0 pointer-events-none opacity-[0.03] z-0" 
        style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E\")" }} 
      />

      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-black/40 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <Button variant="ghost" onClick={() => navigate('/')} className="text-gray-300 hover:text-white hover:bg-white/5 rounded-full" data-testid="back-btn">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Home
          </Button>
          <Button onClick={() => navigate('/signup')} className="rounded-full bg-gradient-to-r from-violet-500 to-purple-500 hover:shadow-lg hover:shadow-violet-500/25 transition-all" data-testid="get-started-btn">
            Get Started <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-block px-4 py-2 rounded-full bg-violet-500/20 text-violet-300 text-sm font-medium mb-6">
              COMPLETE GUIDE
            </span>
            <h1 className="text-4xl md:text-6xl font-bold mb-6 tracking-tight">
              Master <span className="bg-gradient-to-r from-violet-400 to-pink-400 bg-clip-text text-transparent">SharePay</span> in 5 Steps
            </h1>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto mb-8">
              From creating your first group to celebrating your savings goal. Everything you need to know, explained simply.
            </p>
          </motion.div>

          {/* Quick Features */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="flex flex-wrap justify-center gap-4 mt-8"
          >
            {features.map((f, i) => (
              <div key={i} className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10">
                <span className="text-violet-400">{f.icon}</span>
                <span className="text-sm font-medium">{f.title}</span>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Step-by-Step Guide */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-violet-400 text-sm font-bold tracking-[0.2em] uppercase">How It Works</span>
            <h2 className="text-3xl md:text-4xl font-bold mt-4">Step-by-Step Guide</h2>
          </div>

          {/* Steps Navigation */}
          <div className="flex justify-center gap-2 mb-12 flex-wrap">
            {steps.map((step, i) => (
              <button
                key={i}
                onClick={() => setActiveStep(i)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  activeStep === i 
                    ? 'bg-gradient-to-r from-violet-500 to-purple-500 text-white shadow-lg shadow-violet-500/25' 
                    : 'bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white'
                }`}
              >
                Step {step.number}
              </button>
            ))}
          </div>

          {/* Active Step Detail */}
          <motion.div
            key={activeStep}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="grid md:grid-cols-2 gap-8 items-center"
          >
            {/* Left: Visual */}
            <div className={`relative rounded-3xl bg-gradient-to-br ${steps[activeStep].color} p-1`}>
              <div className="rounded-3xl bg-[#030712] p-8 md:p-12">
                <div className="flex items-center gap-4 mb-6">
                  <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${steps[activeStep].color} flex items-center justify-center`}>
                    {steps[activeStep].icon}
                  </div>
                  <div>
                    <span className="text-gray-500 text-sm font-bold">STEP {steps[activeStep].number}</span>
                    <h3 className="text-2xl font-bold">{steps[activeStep].title}</h3>
                  </div>
                </div>
                <p className="text-gray-400 text-lg leading-relaxed">
                  {steps[activeStep].description}
                </p>
              </div>
            </div>

            {/* Right: Details */}
            <div className="space-y-4">
              <h4 className="text-lg font-semibold text-gray-300 mb-4">How to do it:</h4>
              {steps[activeStep].details.map((detail, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.1 }}
                  className="flex items-start gap-4 p-4 rounded-xl bg-white/5 border border-white/5 hover:border-white/10 transition-all"
                >
                  <div className={`w-8 h-8 rounded-full bg-gradient-to-br ${steps[activeStep].color} flex items-center justify-center shrink-0`}>
                    <span className="text-sm font-bold">{i + 1}</span>
                  </div>
                  <p className="text-gray-300">{detail}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Step Navigation Arrows */}
          <div className="flex justify-center gap-4 mt-12">
            <Button
              variant="ghost"
              onClick={() => setActiveStep(Math.max(0, activeStep - 1))}
              disabled={activeStep === 0}
              className="rounded-full bg-white/5 hover:bg-white/10 disabled:opacity-30"
            >
              <ArrowLeft className="w-4 h-4 mr-2" /> Previous
            </Button>
            <Button
              onClick={() => setActiveStep(Math.min(steps.length - 1, activeStep + 1))}
              disabled={activeStep === steps.length - 1}
              className="rounded-full bg-gradient-to-r from-violet-500 to-purple-500 disabled:opacity-30"
            >
              Next <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section className="py-20 px-4 bg-gradient-to-b from-transparent to-violet-950/20">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-pink-400 text-sm font-bold tracking-[0.2em] uppercase">Use Cases</span>
            <h2 className="text-3xl md:text-4xl font-bold mt-4">What Can You Use It For?</h2>
            <p className="text-gray-400 mt-4 max-w-2xl mx-auto">
              From splitting rent to planning dream vacations, SharePay adapts to your needs.
            </p>
          </div>

          {/* Use Case Tabs */}
          <div className="flex justify-center gap-2 mb-12 flex-wrap">
            {useCases.map((uc, i) => (
              <button
                key={i}
                onClick={() => setActiveUseCase(i)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  activeUseCase === i 
                    ? `bg-gradient-to-r ${uc.color} text-white shadow-lg` 
                    : 'bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white'
                }`}
              >
                {uc.icon}
                <span className="hidden sm:inline">{uc.title}</span>
              </button>
            ))}
          </div>

          {/* Active Use Case */}
          <motion.div
            key={activeUseCase}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4 }}
            className={`rounded-3xl ${useCases[activeUseCase].gradient} border border-white/10 p-8 md:p-12`}
          >
            <div className="grid md:grid-cols-2 gap-8">
              {/* Left */}
              <div>
                <div className={`inline-flex items-center gap-3 px-4 py-2 rounded-full bg-gradient-to-r ${useCases[activeUseCase].color} text-white mb-6`}>
                  {useCases[activeUseCase].icon}
                  <span className="font-bold">{useCases[activeUseCase].title}</span>
                </div>
                <h3 className="text-2xl md:text-3xl font-bold mb-4">{useCases[activeUseCase].subtitle}</h3>
                <p className="text-gray-300 text-lg leading-relaxed mb-6">
                  {useCases[activeUseCase].description}
                </p>
                <div className="p-4 rounded-xl bg-black/30 border border-white/10">
                  <span className="text-gray-500 text-sm">Example:</span>
                  <p className="text-white font-medium mt-1">{useCases[activeUseCase].example}</p>
                </div>
              </div>

              {/* Right: Tips */}
              <div>
                <h4 className="text-lg font-semibold text-gray-300 mb-4">Pro Tips:</h4>
                <div className="space-y-3">
                  {useCases[activeUseCase].tips.map((tip, i) => (
                    <div key={i} className="flex items-start gap-3 p-4 rounded-xl bg-black/20 border border-white/5">
                      <CheckCircle className={`w-5 h-5 shrink-0 bg-gradient-to-r ${useCases[activeUseCase].color} rounded-full`} />
                      <p className="text-gray-300">{tip}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="rounded-3xl bg-gradient-to-r from-violet-600 to-pink-600 p-12 relative overflow-hidden"
          >
            {/* Background decoration */}
            <div className="absolute inset-0 opacity-30">
              <div className="absolute top-0 right-0 w-64 h-64 rounded-full bg-white/20 blur-3xl -translate-y-1/2 translate-x-1/2" />
              <div className="absolute bottom-0 left-0 w-48 h-48 rounded-full bg-white/20 blur-3xl translate-y-1/2 -translate-x-1/2" />
            </div>

            <div className="relative z-10">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">Ready to Start Saving?</h2>
              <p className="text-white/80 text-lg mb-8 max-w-xl mx-auto">
                Join thousands of Indians who are managing their group finances smarter. It's free to start!
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  onClick={() => navigate('/signup')}
                  className="rounded-full bg-white text-violet-600 hover:bg-gray-100 font-bold px-8 py-6 text-lg"
                  data-testid="cta-signup-btn"
                >
                  Create Free Account
                </Button>
                <Button 
                  onClick={() => navigate('/support')}
                  variant="ghost"
                  className="rounded-full border-2 border-white/30 text-white hover:bg-white/10 px-8 py-6 text-lg"
                >
                  Need Help?
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 border-t border-white/10">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-gray-500 text-sm">© 2026 SharePay. All rights reserved.</p>
          <div className="flex gap-6 text-sm text-gray-500">
            <a href="/privacy" className="hover:text-white transition-colors">Privacy</a>
            <a href="/terms" className="hover:text-white transition-colors">Terms</a>
            <a href="/support" className="hover:text-white transition-colors">Support</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Guide;
